import {
  Alert,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Tag,
  Typography,
  message,
} from 'antd';
import React, { useState } from 'react';
import { useMutation } from 'react-apollo';
import {
  ADMIN_INVESTMENT_WITHDRAW,
  ADMIN_TREASURY_DEPOSIT,
  ADMIN_TREASURY_WITHDRAW,
} from '../../pages/apollo_mutations';
import {
  MONEY_INPUT,
  REASON_RULES,
  backendMessage,
  fmtMoney,
} from '../investment-rules/shared';

// ONE modal per direction, never a toggle: the legacy switch showed "Fund" while the button said
// "Withdraw" (its label and its `checked` were wired backwards), so the operator could not trust what
// they were about to do. Here the direction is fixed when the modal opens and repeated in the title,
// the OK button and the summary line.
export const DIRECTIONS = {
  deposit: {
    title: 'Fund the treasury',
    verb: 'Fund',
    from: 'the selected bank',
    to: "Constitution's wallet",
    mutation: ADMIN_TREASURY_DEPOSIT,
    field: 'adminTreasuryDeposit',
    okType: 'primary',
  },
  withdraw: {
    title: 'Withdraw from the treasury',
    verb: 'Withdraw',
    from: "Constitution's wallet",
    to: 'the selected bank',
    mutation: ADMIN_TREASURY_WITHDRAW,
    field: 'adminTreasuryWithdraw',
    okType: 'danger',
  },
  // The legacy note page's "Withdraw": the money investors put into the note leaves its wallet to a bank
  // of the house, to be lent. `extra` = the variables this direction adds to the input (the note).
  noteWithdraw: {
    title: 'Withdraw from the note wallet',
    verb: 'Withdraw',
    from: "the note's wallet",
    to: 'the selected bank of Constitution',
    mutation: ADMIN_INVESTMENT_WITHDRAW,
    field: 'adminInvestmentWithdraw',
    okType: 'danger',
  },
};

// "Chase ••••1234 · active" — what the operator recognises; the value is Sila's `accountName`.
export const bankLabel = (bank) =>
  [
    bank.providerName || 'Bank',
    bank.accountNumber ? `••••${`${bank.accountNumber}`.slice(-4)}` : null,
    bank.accountStatus || null,
  ]
    .filter(Boolean)
    .join(' · ');

const positiveAmount = {
  validator: (_, value) =>
    typeof value === 'number' && value > 0
      ? Promise.resolve()
      : Promise.reject(new Error('Amount must be greater than 0')),
};

// `extra`: variables the direction adds to the input (e.g. `{ investmentId }`). `available`: the live
// balance the amount may not exceed — advisory on screen, the backend enforces it against Sila.
// `topSlot`: what the parent puts ABOVE the form (the source picker of the treasury page); `okDisabled`
// lets it hold the OK button until that choice is usable.
export function MovementModal({
  direction,
  banks,
  onClose,
  onDone,
  extra = {},
  available,
  topSlot = null,
  okDisabled = false,
  sourceLabel,
}) {
  // A source that cannot pay (no wallet, Sila unreachable) blocks the WHOLE form, not only the button:
  // nothing typed there could ever be sent (TL, 2026-09-29).
  const formDisabled = okDisabled;
  const spec = DIRECTIONS[direction];
  const [form] = Form.useForm();
  const [error, setError] = useState(null);
  // The validated form, waiting for the operator's second look (TL, 2026-09-29: a summary to confirm,
  // instead of typing the amount twice). `null` = still editing.
  const [review, setReview] = useState(null);
  const [move, { loading }] = useMutation(spec.mutation);

  const bank = (values) => {
    const found = banks.find((b) => b.accountName === values.bankAccountName);
    return found ? bankLabel(found) : values.bankAccountName;
  };
  // Who pays and who receives, in the operator's words.
  const house = "Constitution's wallet (Treasury)";
  const endpoints = (values) =>
    direction === 'deposit'
      ? { from: bank(values), to: house }
      : {
          from:
            direction === 'noteWithdraw'
              ? `${sourceLabel || 'The note'} · note wallet`
              : house,
          to: bank(values),
        };

  const submit = async (values) => {
    setError(null);
    try {
      const { data } = await move({
        variables: {
          data: {
            ...extra,
            bankAccountName: values.bankAccountName,
            amount: values.amount,
            reason: values.reason.trim(),
          },
        },
      });
      const movement = data[spec.field];
      message.success(
        `${spec.verb} of ${fmtMoney(movement.amount)} sent to Sila · ${
          movement.status
        }`
      );
      // Close FIRST: the page reload behind asks Sila four things and takes seconds, and an accepted
      // movement must not keep a filled form on screen while that happens.
      setReview(null);
      onClose();
      onDone();
    } catch (e) {
      setError(backendMessage(e));
    }
  };

  const summary = review && endpoints(review);
  const confirmModal = review && (
    <Modal
      visible
      title={`Confirm ${direction === 'deposit' ? 'fund' : 'withdrawal'}`}
      okText={`Confirm · ${spec.verb} ${fmtMoney(review.amount)}`}
      okType={spec.okType}
      okButtonProps={{ loading }}
      cancelText="Back"
      cancelButtonProps={{ disabled: loading }}
      onOk={() => submit(review)}
      onCancel={() => {
        setReview(null);
        setError(null);
      }}
      closable={!loading}
      maskClosable={false}
      width={560}
    >
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 16 }}
        />
      )}
      <Descriptions column={1} bordered size="middle">
        <Descriptions.Item label="Movement">
          <Tag color={direction === 'deposit' ? 'blue' : 'volcano'}>
            {direction === 'deposit' ? 'Fund' : 'Withdraw'}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="From">{summary.from}</Descriptions.Item>
        <Descriptions.Item label="To">{summary.to}</Descriptions.Item>
        <Descriptions.Item label="Amount">
          <Typography.Text strong style={{ fontSize: 20 }}>
            {fmtMoney(review.amount)}
          </Typography.Text>
        </Descriptions.Item>
        {typeof available === 'number' && direction !== 'deposit' && (
          <Descriptions.Item label="Left in the source">
            {fmtMoney(Math.max(0, available - review.amount))}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Reason">
          {review.reason.trim()}
        </Descriptions.Item>
      </Descriptions>
      <Typography.Paragraph
        type="secondary"
        style={{ marginTop: 16, marginBottom: 0 }}
      >
        Real money moves through Sila when you confirm. It leaves as pending and
        settles when the bank transfer clears; it cannot be cancelled from this
        screen.
      </Typography.Paragraph>
    </Modal>
  );

  return (
    <>
      {confirmModal}
      <Modal
        visible
        title={spec.title}
        okText="Review"
        okType="primary"
        okButtonProps={{ disabled: okDisabled }}
        onOk={() => form.submit()}
        onCancel={onClose}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Money moves from {spec.from} to {spec.to}. It leaves as pending and
          settles when Sila confirms it; the status refreshes every time this
          page loads.
        </Typography.Paragraph>
        {topSlot}
        <Form
          form={form}
          disabled={formDisabled}
          layout="vertical"
          // Valid → the summary to confirm; nothing is sent from this form directly.
          onFinish={(values) => {
            setError(null);
            setReview(values);
          }}
          preserve={false}
          onValuesChange={() => setError(null)}
        >
          <Form.Item
            name="bankAccountName"
            label="Bank account"
            rules={[{ required: true, message: 'Pick the bank' }]}
          >
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a bank linked to the treasury"
            >
              {banks.map((bank) => (
                <Select.Option key={bank.accountName} value={bank.accountName}>
                  {bankLabel(bank)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="amount"
            label="Amount"
            extra={
              typeof available === 'number'
                ? `Available in the source: ${fmtMoney(available)}`
                : undefined
            }
            rules={[
              positiveAmount,
              {
                validator: (_, value) =>
                  typeof available === 'number' &&
                  typeof value === 'number' &&
                  value > available
                    ? Promise.reject(
                        new Error(
                          `Exceeds the available ${fmtMoney(available)}`
                        )
                      )
                    : Promise.resolve(),
              },
            ]}
          >
            <InputNumber {...MONEY_INPUT} autoFocus />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Reason (audited)"
            rules={REASON_RULES}
          >
            <Input.TextArea
              rows={3}
              placeholder={
                direction === 'deposit'
                  ? 'e.g. Funding October interest payments'
                  : 'e.g. Sweep of idle balance back to operating account'
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
